import { downloadRenderedPrenatalCsv, printRenderedPrenatal } from './export';
import { Fragment, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatInstant } from '@/lib/utils/date';
import { prenatalDefinition, type FlowsheetDefinition } from './definitions';
import { renderFlowsheet, cellValue } from './renderModel';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from '@/components/ui/dialog';
import { displayPayload } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
export function PrenatalGrid({
  events,
  subjects,
  edd,
  timeZone,
  definition = prenatalDefinition,
  flags = [],
  onEdit,
  exportContext,
  prePregnancyWeightGrams,
}: {
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  edd: string | null;
  timeZone: string;
  definition?: FlowsheetDefinition;
  flags?: { eventId: string; severity: string; message: string }[];
  onEdit?: (event: ClinicalEvent) => void;
  exportContext?: string;
  prePregnancyWeightGrams?: number | null;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [numbersOnly, setNumbersOnly] = useState(false);
  const [selected, setSelected] = useState<ClinicalEvent | null>(null);
  const { rows: projected, columns } = renderFlowsheet(events, subjects, timeZone, edd, definition, numbersOnly);
  const values = projected.map((r) => r.events);
  return (
    <div className="space-y-3">
      <Button variant="outline" aria-pressed={numbersOnly} onClick={() => setNumbersOnly((v) => !v)}>
        {numbersOnly ? 'Show all observations' : 'Numbers only'}
      </Button>
      {exportContext && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() =>
              downloadRenderedPrenatalCsv(events, subjects, timeZone, {
                edd,
                definition,
                numbersOnly,
                prePregnancyWeightGrams,
              })
            }>
            Export displayed grid CSV
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              printRenderedPrenatal(events, subjects, timeZone, exportContext, {
                edd,
                definition,
                numbersOnly,
                prePregnancyWeightGrams,
              })
            }>
            Print displayed grid / save PDF
          </Button>
        </div>
      )}
      <div className="max-h-[70vh] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-20 bg-background">
            <TableRow>
              <TableHead className="sticky left-0 z-30 bg-background">Visit observations</TableHead>
              <TableHead>GA</TableHead>
              {columns.map((c) => (
                <TableHead key={`${c.column.key}/${c.subjectId}`}>{c.label}</TableHead>
              ))}
              <TableHead>Recorded by</TableHead>
              <TableHead>Signature status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {values.map((row, rowIndex) => (
              <Fragment key={projected[rowIndex].id}>
                <TableRow id={`prenatal-visit-${row[0].encounter_id}`} tabIndex={-1}>
                  <TableCell className="sticky left-0 z-10 bg-background">
                    <div className="flex flex-col gap-2">
                      {formatInstant(row[0].occurred_at, 'MMM d, yyyy HH:mm', timeZone)}
                      <Button
                        type="button"
                        variant="outline"
                        aria-expanded={expanded.has(row[0].encounter_id)}
                        aria-controls={`visit-details-${row[0].encounter_id}`}
                        onClick={() =>
                          setExpanded((current) => {
                            const next = new Set(current);
                            if (next.has(row[0].encounter_id)) next.delete(row[0].encounter_id);
                            else next.add(row[0].encounter_id);
                            return next;
                          })
                        }>
                        {expanded.has(row[0].encounter_id) ? 'Hide details' : 'Show details'}
                      </Button>
                      <a
                        href={`#flowsheet-encounter-${row[0].encounter_id}`}
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent('flowsheet-open-encounter', { detail: row[0].encounter_id }),
                          )
                        }>
                        Open encounter
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>{projected.find((r) => r.events === row)?.ga}</TableCell>
                  {columns.map(({ column, subjectId }) => {
                    const matches = row.filter(
                      (e) =>
                        e.event_type === column.eventType &&
                        e.subject_id === subjectId &&
                        (column.schemaVersion === undefined || e.schema_version === column.schemaVersion),
                    );
                    return (
                      <TableCell key={`${column.key}/${subjectId}`}>
                        {matches.map((event) => {
                          const value = cellValue(event, column, prePregnancyWeightGrams);
                          return (
                            <div key={event.id} className="flex flex-col gap-1">
                              <Button
                                variant="ghost"
                                className="h-auto min-h-11 justify-start whitespace-normal text-left"
                                onClick={() => setSelected(event)}>
                                {String(value)}
                              </Button>
                              {flags
                                .filter((f) => f.eventId === event.id)
                                .map((f, i) => (
                                  <span
                                    key={i}
                                    className={
                                      f.severity === 'critical'
                                        ? 'font-semibold text-red-700'
                                        : f.severity === 'warn'
                                          ? 'font-semibold text-amber-800'
                                          : 'text-muted-foreground'
                                    }>
                                    ⚠ {f.severity}: {f.message}
                                  </span>
                                ))}
                              <small className="text-muted-foreground">
                                {formatInstant(event.occurred_at, 'HH:mm', timeZone)} · {event.author_initials}
                              </small>
                            </div>
                          );
                        })}
                        {!matches.length && '—'}
                      </TableCell>
                    );
                  })}
                  <TableCell>{[...new Set(row.map((e) => e.author_initials))].join(', ')}</TableCell>
                  <TableCell>
                    {row.filter((event) => event.signed).length} of {row.length} observations signed
                  </TableCell>
                </TableRow>
                {expanded.has(row[0].encounter_id) && (
                  <TableRow id={`visit-details-${row[0].encounter_id}`}>
                    <TableCell colSpan={columns.length + 4}>
                      <ul className="flex flex-col gap-3">
                        {row.map((event) => (
                          <li key={event.id} className="whitespace-pre-wrap rounded border p-3">
                            <strong>
                              {subjects.find((subject) => subject.id === event.subject_id)?.label ?? 'Unknown subject'}{' '}
                              · {event.event_type}
                            </strong>
                            <p>{displayPayload(event.payload)}</p>
                            <p>
                              {formatInstant(event.occurred_at, 'MMM d, yyyy HH:mm:ss', timeZone)} ·{' '}
                              {event.author_initials} · {event.source} · {event.signed ? 'Signed' : 'Unsigned'}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      {!values.length && (
        <p className="py-4 text-muted-foreground">No confirmed observations. Blank cells mean not documented.</p>
      )}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Observation details</DialogTitle>
            <DialogDescription>Current saved finding and its provenance</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <p>
                {subjects.find((s) => s.id === selected.subject_id)?.label} · {selected.event_type}
              </p>
              <p className="whitespace-pre-wrap">{displayPayload(selected.payload)}</p>
              <p>Observed {formatInstant(selected.occurred_at, 'MMM d, yyyy HH:mm:ss XXX', timeZone)}</p>
              <p>Recorded {formatInstant(selected.recorded_at, 'MMM d, yyyy HH:mm:ss XXX', timeZone)}</p>
              <p>
                {selected.author_initials} · {selected.author_role} · {selected.source} ·{' '}
                {selected.signed ? 'Signed' : 'Unsigned'}
              </p>
              {onEdit ? (
                <Button
                  onClick={() => {
                    onEdit(selected);
                    setSelected(null);
                  }}>
                  Amend or strike in history
                </Button>
              ) : (
                <p>Use observation history below to amend or strike this finding.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
